/**
 * Composer parity (leg `v2-composer-parity`) — turning the OLD composer's `useAttachments` state
 * into the wire shape `POST /sessions/turns` accepts (`AttachmentIn`, `laneV2/types.ts`).
 *
 * This client always sends PRE-RESOLVED attachments: `markdown` is filled by the existing
 * `/api/runs/preprocess` pipeline before a turn is ever submitted (the same pipeline the old
 * composer already runs `useAttachments`/`resolveAttachment` through), so there is no reason to
 * duplicate that resolution server-side by sending raw `content`/`data_base64`.
 *
 * Pure, so the "only ready, non-empty attachments ride the turn" rule is unit-testable without a
 * component.
 */
import type { MultiAgentAttachment } from "../api";
import { clampAttachmentText } from "./contextFile";
import type { AttachmentIn } from "./types";

export function toAttachmentIn(a: Pick<MultiAgentAttachment, "kind" | "name" | "mime" | "markdown" | "language">): AttachmentIn {
  // MSA caps one attachment at 200,000 characters (413 otherwise): over it, cut with a visible marker.
  const markdown = typeof a.markdown === "string" ? clampAttachmentText(a.markdown) : a.markdown;
  return { kind: a.kind, name: a.name, mime: a.mime, markdown, language: a.language };
}

/** Only attachments that finished resolving with real content — a still-processing or failed one
 *  is never silently sent as empty. */
export function sendableAttachments(attachments: readonly MultiAgentAttachment[]): MultiAgentAttachment[] {
  return attachments.filter((a) => a.status === "ready" && !!a.markdown?.trim());
}

export function sendableAttachmentIns(attachments: readonly MultiAgentAttachment[]): AttachmentIn[] {
  return sendableAttachments(attachments).map(toAttachmentIn);
}
